import { useCallback, useEffect, useRef } from 'react';

// ─── Swipe entre páginas ─────────────────────────────────────────────────────
// Arrastar o dedo na horizontal (fora de áreas que rolam na horizontal) troca
// de página no mobile. Regras para não roubar gesto de ninguém:
//
// - Só toque, um dedo. Mouse nunca troca de página (arrastar com o cursor é
//   seleção de texto ou rolagem);
// - O gesto só começa quando o eixo horizontal vence a perpendicular, então a
//   rolagem vertical da página continua nativa;
// - `[data-no-swipe]`, controles de formulário, dialogs e qualquer ancestral
//   que role na horizontal (tabelas, grids, chips) são ignorados;
// - **O `touchmove` do gesto é consumido** (`preventDefault` em listener
//   não-passivo). Sem isso o Chromium mantém a sequência de toque "viva" e
//   **suprime o `click` do próximo toque** por ~300-500 ms — o mesmo "preciso
//   tocar duas vezes" documentado em docs/MOBILE.md.

/** Movimento mínimo (px) para decidir o eixo do gesto. */
const TOLERANCIA = 10;
/** Quanto o eixo horizontal precisa vencer a perpendicular. */
const RAZAO_EIXO = 1.2;
/** Distância (px) que troca de página. */
const LIMITE_PADRAO = 64;
/** Velocidade (px/ms) que troca mesmo sem percorrer o limite completo. */
const VELOCIDADE_MINIMA = 0.5;
/** Piso da troca por velocidade: um tremor nunca vira navegação. */
const MINIMO_POR_VELOCIDADE = 32;
/** Janela mínima do cálculo de velocidade (evita divisão explosiva). */
const JANELA_VELOCIDADE_MS = 12;

/** Onde o gesto nunca começa. */
const ALVOS_BLOQUEADOS = [
  '[data-no-swipe]',
  '[role="dialog"]',
  'form',
  'input',
  'textarea',
  'select',
  '[contenteditable="true"]',
  '[role="textbox"]',
].join(', ');

/**
 * O toque começou em algo que rola na horizontal?
 *
 * Tabela larga, grid de arquivos ou faixa de chips rolam "para o lado" com o
 * mesmo arrasto que trocaria de página: ali a rolagem é do elemento, não nossa.
 */
const rolaNaHorizontal = (alvo) => {
  if (typeof window === 'undefined' || typeof window.getComputedStyle !== 'function') return false;

  let node = alvo;

  while (node && node.nodeType === 1) {
    const { overflowX } = window.getComputedStyle(node);
    const rola = overflowX === 'auto' || overflowX === 'scroll' || overflowX === 'overlay';

    if (rola && node.scrollWidth > node.clientWidth + 1) return true;

    node = node.parentElement;
  }

  return false;
};

/**
 * @param {object} opcoes
 * @param {boolean} [opcoes.ativo] Liga/desliga o gesto (desligado não registra nada).
 * @param {(direcao: 'proximo'|'anterior') => void} opcoes.onNavegar Destino do gesto.
 * @param {number} [opcoes.limite] Distância (px) para trocar de página.
 * @returns {{ ref: (node: HTMLElement|null) => void }} `ref` obrigatório na área de swipe.
 */
export const useSwipeEntrePaginas = ({ ativo = true, onNavegar, limite = LIMITE_PADRAO } = {}) => {
  const navegarRef = useRef(onNavegar);
  /** Desliga os listeners do elemento atual (ver `ref`). */
  const desligarRef = useRef(null);

  useEffect(() => {
    navegarRef.current = onNavegar;
  }, [onNavegar]);

  const ref = useCallback(
    (node) => {
      desligarRef.current?.();
      desligarRef.current = null;

      if (!ativo || !node || typeof node.addEventListener !== 'function') return;

      let gesto = null;

      const aoIniciar = (event) => {
        gesto = null;
        if (event.touches?.length !== 1) return;

        const alvo = event.target;
        if (alvo?.closest?.(ALVOS_BLOQUEADOS)) return;
        if (rolaNaHorizontal(alvo)) return;

        const toque = event.touches[0];
        gesto = {
          x: toque.clientX,
          y: toque.clientY,
          inicio: Date.now(),
          eixo: null,
          deslocamento: 0,
        };
      };

      const aoMover = (event) => {
        if (!gesto || event.touches?.length !== 1) return;

        const toque = event.touches[0];
        const dx = toque.clientX - gesto.x;
        const dy = toque.clientY - gesto.y;

        if (gesto.eixo === null) {
          if (Math.hypot(dx, dy) < TOLERANCIA) return;

          // Vertical: a rolagem é do navegador. O gesto é encerrado (e não
          // volta a ser candidato) para não capturar o fim de uma rolagem.
          if (Math.abs(dx) <= Math.abs(dy) * RAZAO_EIXO) {
            gesto = null;
            return;
          }

          gesto.eixo = 'horizontal';
        }

        gesto.deslocamento = dx;

        if (event.cancelable) event.preventDefault();
      };

      const aoTerminar = () => {
        const atual = gesto;
        gesto = null;

        if (!atual || atual.eixo !== 'horizontal') return;

        const distancia = Math.abs(atual.deslocamento);
        const duracao = Math.max(JANELA_VELOCIDADE_MS, Date.now() - atual.inicio);
        const velocidade = distancia / duracao;
        const trocouPelaVelocidade =
          velocidade >= VELOCIDADE_MINIMA && distancia >= MINIMO_POR_VELOCIDADE;

        if (distancia < limite && !trocouPelaVelocidade) return;

        // Um gesto, uma navegação: o estado já foi zerado acima.
        navegarRef.current?.(atual.deslocamento < 0 ? 'proximo' : 'anterior');
      };

      node.addEventListener('touchstart', aoIniciar, { passive: true });
      node.addEventListener('touchmove', aoMover, { passive: false });
      node.addEventListener('touchend', aoTerminar);
      node.addEventListener('touchcancel', aoTerminar);

      desligarRef.current = () => {
        node.removeEventListener('touchstart', aoIniciar);
        node.removeEventListener('touchmove', aoMover);
        node.removeEventListener('touchend', aoTerminar);
        node.removeEventListener('touchcancel', aoTerminar);
        gesto = null;
      };
    },
    [ativo, limite]
  );

  useEffect(
    () => () => {
      desligarRef.current?.();
      desligarRef.current = null;
    },
    []
  );

  return { ref };
};

export default useSwipeEntrePaginas;
